import { Link, Navigate } from "react-router"
import { useTranslation } from "react-i18next"
import { useCallback, useEffect, useState } from "react"
import { Button } from "@repo/ui"
import { Spinner } from "@phosphor-icons/react"
import {
  useGetUserQuery,
  useResendVerificationEmailMutation,
} from "@/feature/auth/api"
import { LogoExtended } from "@/routes/layouts/public-layout"
import { useAuthStore } from "@/feature/auth/store/authStore"

export const ResendMail = (props: { email: string; ctx?: "pending" }) => {
  const { t } = useTranslation(`wizard`)
  const [sended, setSended] = useState(false)
  const resendVerificationEmailMutation = useResendVerificationEmailMutation()

  const resendMail = useCallback(() => {
    resendVerificationEmailMutation.mutate(props.email)
  }, [resendVerificationEmailMutation, props.email])

  const { setSession, setUser } = useAuthStore()

  useEffect(() => {
    if (resendVerificationEmailMutation.isSuccess) {
      setSended(true)
    }
  }, [resendVerificationEmailMutation.isSuccess, setSession, setUser])

  return (
    <div className="bg-white w-full p-8 h-full flex flex-col items-center justify-between">
      <LogoExtended />
      <div className="max-w-md">
        <div className="flex flex-col items-center justify-between gap-8">
          <h1 className="text-4xl font-medium text-center">
            {t(`sign_up.screens.validation.title`, {
              context: props.ctx,
            })}
          </h1>
          <div className="flex flex-col gap-4">
            <p className="text-center text-muted-foreground">
              {t(`sign_up.screens.validation.description1`, {
                user_mail: props.email,
                context: props.ctx,
              })}
            </p>
            <p className="text-center text-muted-foreground">
              {t(`sign_up.screens.validation.description2`, {
                context: props.ctx,
              })}
            </p>
          </div>
        </div>

        <div className="flex flex-col mt-10 gap-2">
          <Button disabled={sended} className="w-full" onClick={resendMail}>
            {t(`sign_up.screens.validation.button`)}
          </Button>
          {sended ? (
            <p className="text-center text-success text-sm">
              {t(`sign_up.screens.validation.alt_text`)}
            </p>
          ) : (
            <div className="h-4" />
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 justify-center">
        <p className="text-muted-foreground">
          {t(`sign_up.screens.welcome.login.text`)}
        </p>
        <Link to="/auth/login" className="primary-link">
          {t(`sign_up.screens.welcome.login.button`)}
        </Link>
      </div>
    </div>
  )
}

export const ResendEmailForPendingUser = () => {
  const { data: userData, isLoading, isFetching } = useGetUserQuery()
  if (isLoading || isFetching) {
    return (
      <div className="bg-white w-full p-8 h-full flex flex-col items-center justify-between">
        <Spinner className="w-10 h-10 animate-spin" />
      </div>
    )
  }
  if (userData?.status === `pending`) {
    return <ResendMail email={userData.email} ctx="pending" />
  }
  return <Navigate to="/" />
}

export const ResendEmailNotAuthenticated = () => {
  const user_mail = localStorage.getItem(`sign_up_mail`) ?? `missingmail@mail`
  return <ResendMail email={user_mail} />
}
